import { BaseModal } from "../components/common/BaseModal/BaseModal";
import { useEnvJSON } from "../configurator/renderer/customContext/EnvContext";
import { useUiTranslator } from "../hooks/useUiTranslator";

import { JSX } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";

interface EasySessionConversionErrorProps {
  /** Backend error message, already translated */
  detail?: string;
  isRetrying: boolean;
  onRetry: () => void;
}

/**
 * Blocking dialog shown when the session handed over from EASY
 * (`?sessionFromEasy=`) could not be converted. The user otherwise ends up
 * in an anonymous cart without noticing that their EASY quote is missing.
 */
export function EasySessionConversionError({
  detail,
  isRetrying,
  onRetry,
}: EasySessionConversionErrorProps): JSX.Element {
  const t = useUiTranslator();
  const { envJSON } = useEnvJSON();
  const easyUrl = envJSON?.APP_EASY_URL || import.meta.env.VITE_APP_EASY_URL;

  return (
    <BaseModal
      title={t(
        "CustomApp:dialog.easySessionConversionError.header",
        "Your EASY session could not be opened",
      )}
      isOpen={true}
      onClose={() => {}}
      hideClose={true}
    >
      <Box display="flex" gap={2} sx={{ maxWidth: "32em" }}>
        <ErrorOutlineIcon color="error" fontSize="large" />
        <Box>
          <Typography variant="body1">
            {t(
              "CustomApp:dialog.easySessionConversionError.body",
              "The quote you started in EASY could not be transferred. The link may have expired or already been used. Please try again or return to EASY and open the configurator from there.",
            )}
          </Typography>
          {detail ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {detail}
            </Alert>
          ) : null}
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={2} pt={4}>
        <Button variant="outlined" onClick={onRetry} disabled={isRetrying}>
          {isRetrying ? (
            <CircularProgress size="1em" sx={{ mr: ".5em" }} />
          ) : null}
          {t("CustomApp:btn.retry", "Retry")}
        </Button>
        {easyUrl ? (
          <Button variant="contained" href={easyUrl} disabled={isRetrying}>
            {t("CustomApp:btn.backToEasy", "Back to EASY")}
          </Button>
        ) : null}
      </Box>
    </BaseModal>
  );
}
